export const CHECKOUT_URL = "https://bioroom-en.impultienda.ar/checkout/";

export const images = {
  product: "https://cdn.magicpatterns.com/uploads/qRNSL8fnAv1fBJX4HHYrGo/BIOROOM-EN.png",
  author: "https://cdn.magicpatterns.com/uploads/aoKyvdjZwoPZp1Vm88f2MG/VALERIA_MONTES_-_BIOMECANICA.png",
};

export const studiedConcepts = [
  "Forces",
  "Moments",
  "Anchorage",
  "Centers of resistance",
  "Segmented mechanics",
  "Root control",
  "Vertical control",
  "Rotational control",
];

export const hardQuestions = [
  "Do you know which variable you should analyze first?",
  "Can you identify the actual biomechanical objective?",
  "Can you recognize the restrictions that may affect your reasoning?",
  "Can you anticipate potential unwanted effects?",
  "Can you explain why one conceptual approach makes more sense than another?",
];

export const beforeItems = [
  "You study concepts separately.",
  "You read about one mechanic and then another.",
  "You memorize techniques.",
  "You try to remember what happened in previous cases.",
  "A complex case appears — and you have a lot of information, but no clear starting point.",
  "Sometimes you only discover what you missed after making a decision.",
];

export const afterItems = [
  "You work through structured clinical scenarios.",
  "You define the biomechanical objective first.",
  "You identify the relevant variables.",
  "You consider the restrictions.",
  "You compare different conceptual approaches.",
  "You identify potential unwanted effects — then review the reasoning behind the case.",
];

export type PainIcon = "alert" | "clock" | "help" | "brain" | "rewind" | "compass";

export const painPoints: { icon: PainIcon; text: string }[] = [
  { icon: "alert", text: "You understand the theory, but when presented with a complex case, you're not always sure what you should analyze first." },
  { icon: "clock", text: "You spend too much time going back through notes, textbooks, lectures, and different resources before you can structure your thinking." },
  { icon: "help", text: "You know what movement you're trying to achieve, but you're not always sure which biomechanical variables deserve your attention first." },
  { icon: "brain", text: "You've studied plenty of mechanics, but you still want more practice developing the judgment required to analyze complex situations." },
  { icon: "rewind", text: "You often learn what you missed only after reviewing a case once the decision has already been made." },
  { icon: "compass", text: "When someone presents you with a difficult case, you know there must be a logical way to approach it — but you don't always know where to begin." },
];

export const practiceLoop = ["Observe", "Analyze", "Form a hypothesis", "Make a decision", "Compare", "Learn"];

export const volumes = [
  {
    numeral: "I",
    title: "The Biomechanist's Mind Map",
    subtitle: "Before you can analyze a complex case, you need to know which questions to ask.",
    intro: "The framework you'll use throughout the War Room:",
    items: ["Objective", "Movement", "Forces", "Moments", "Anchorage", "Restrictions", "Potential side effects", "Control", "Review"],
    closing: "The goal isn't to memorize another formula. It's to learn how to structure the problem before jumping to a solution.",
  },
  {
    numeral: "II",
    title: "The 10-Minute Case Analysis™",
    subtitle: "A repeatable framework for structuring your thinking.",
    intro: "Before looking at the explanation, you'll work through a structured sequence of questions. You'll move from “I don't know where to start” to “I know which variables I need to examine first.”",
    items: [],
    closing: "Includes the 10-Minute Case Analysis™ Worksheet.",
  },
  {
    numeral: "III",
    title: "50 Biomechanical Challenges™",
    subtitle: "This is where the War Room begins — 50 educational case scenarios organized progressively.",
    intro: "Every challenge includes:",
    items: ["Case scenario", "Objective", "Available information", "Main question", "Analysis prompts", "Space for your reasoning", "Conceptual alternatives", "Commented explanation", "Key variables to identify", "Common reasoning mistakes", "Final takeaway"],
    closing: "You don't simply read the answer. First, you decide. Then, you compare. Finally, you learn.",
  },
  {
    numeral: "IV",
    title: "The Cases That Test Your Clinical Reasoning",
    subtitle: "Not every case asks you to think about the same variables.",
    intro: "Educational scenarios involving concepts such as:",
    items: ["Asymmetry", "Anchorage control", "Combined movements", "Root control", "Rotation", "Intrusion and extrusion", "Uprighting", "Space management", "Segmented mechanics", "Unwanted biomechanical effects"],
    closing: "The goal is to train you to recognize which variables deserve consideration before reaching a conclusion.",
  },
  {
    numeral: "V",
    title: "The Biomechanics Error Lab™",
    subtitle: "What happens when the observed result doesn't match what you expected?",
    intro: "Each scenario follows:",
    items: ["Situation", "Warning sign", "Questions", "Possible causes", "Analysis", "Lesson"],
    closing: "Learning biomechanics is just as much about knowing what to question as it is about knowing what to remember.",
  },
  {
    numeral: "VI",
    title: "The Master Review™",
    subtitle: "Consolidate what you've learned once you've worked through the challenges.",
    intro: "This volume includes:",
    items: ["Analysis matrices", "Visual concept maps", "Review checklists", "Reasoning questions", "Comparison tables", "Rapid-review exercises", "Quick cases", "Self-assessments", "Progress tracking", "Personal case-review worksheets"],
    closing: "Use it to revisit the framework and reinforce your reasoning over time.",
  },
];

export type ToolIcon = "brain" | "timer" | "map" | "search" | "clipboard";

export const tools: { icon: ToolIcon; title: string; text: string }[] = [
  { icon: "brain", title: "50 Commented Biomechanical Challenges", text: "Structured scenarios that ask you to think first — and then compare your reasoning with the explanation." },
  { icon: "timer", title: "The 10-Minute Case Analysis™ System", text: "A repeatable worksheet designed to help you organize your analysis before reviewing the answer." },
  { icon: "map", title: "Decision Maps", text: "Visual frameworks that organize the questions you should consider before reaching a conclusion." },
  { icon: "search", title: "Biomechanical Error Detection Framework", text: "A visual tool for identifying potential reasoning gaps and variables you may have overlooked." },
  { icon: "clipboard", title: "Case Review Sheets", text: "Printable worksheets for documenting what you thought, what you identified, and what you learned." },
];

export const bonuses = [
  {
    number: 1,
    title: "Atlas of 50 Biomechanics Maps™",
    value: 47,
    image: "https://cdn.magicpatterns.com/uploads/eFkqmsuJdcvkE42gDzFqUA/BONUS_1_BIOROOM-EN.png",
    text: "A visual collection designed to help you quickly review key biomechanical concepts and relationships. Use it whenever you want to refresh an idea before returning to your case challenges.",
    items: [] as string[],
  },
  {
    number: 2,
    title: "Biomechanics Case Planner™",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/ski7ey2SFcmneWe4Ec4Lor/BONUS_2_BIOROOM-EN.png",
    text: "Professional worksheets designed to turn every case into a structured reasoning exercise. Organize your:",
    items: ["Objective", "Variables", "Restrictions", "Hypotheses", "Potential risks", "Questions", "Review"],
  },
  {
    number: 3,
    title: "Biomechanics Error Library™",
    value: 47,
    image: "https://cdn.magicpatterns.com/uploads/h9MAixoQFUKQximEtBNVGy/BONUS_3_BIOROOM-EN.png",
    text: "A visual collection of scenarios where something didn't go as expected. For each one, you'll ask:",
    items: ["What do you observe?", "What should you review?", "Which variables could explain what you're seeing?"],
  },
  {
    number: 4,
    title: "Quick Decision Cards™",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/c6tD775hc1juhne51ftLAD/BONUS_4_BIOROOM-EN.png",
    text: "Printable visual cards with rapid-fire questions to practice biomechanical reasoning. Perfect for:",
    items: ["Individual study", "Residency programs", "Case discussions", "Study groups", "Teaching sessions", "Review before exams"],
  },
  {
    number: 5,
    title: "30-Minute Biomechanics Challenge™",
    value: 29,
    image: "https://cdn.magicpatterns.com/uploads/qHb4dMoGh3cG6s5H1Z9Ptr/BONUS_5_BIOROOM-EN.png",
    text: "A rapid training session to test your reasoning with short-form cases. 30 minutes. 10 challenges. One mission: can you structure your analysis before looking at the answer?",
    items: [] as string[],
  },
];

export const guaranteeItems = [
  "Explore the volumes",
  "Work through the challenges",
  "Use the worksheets",
  "Review the decision maps",
  "Test the cases",
  "Explore the bonus materials",
];

// Sample testimonials — replace with verified customer testimonials before publishing.
export const testimonials = [
  {
    name: "Dr. Sarah J.",
    role: "Orthodontic Resident",
    flag: "🇺🇸",
    country: "United States",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/fbe93101-c20f-4150-8b16-0e13c4d96c57.jpg",
    quote: "Deciding first and only then reading the explanation completely changed how I study. I now start every case by writing down the objective before anything else.",
  },
  {
    name: "Dr. James W.",
    role: "Orthodontist",
    flag: "🇬🇧",
    country: "United Kingdom",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/881d4bf0-e8a4-4866-ae73-1afb2562771a.jpg",
    quote: "The decision maps are what I keep coming back to. They give me a clear order of questions instead of a pile of concepts.",
  },
  {
    name: "Dr. Priya S.",
    role: "Dentist, Ortho Postgraduate",
    flag: "🇨🇦",
    country: "Canada",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/4db9ff7c-af06-4b88-8938-34376ed5adc0.jpg",
    quote: "The worksheets make each challenge feel like a real case discussion. I print the review sheets and use them with my study group every week.",
  },
  {
    name: "Dr. Liam K.",
    role: "Orthodontic Postgraduate Student",
    flag: "🇦🇺",
    country: "Australia",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/5d5db9a3-4340-4e30-a06d-c98778a7abbb.jpg",
    quote: "Short, focused challenges instead of hours of lectures. It fits between clinic sessions and actually trains the way I think.",
  },
];

export const faqs = [
  { q: "How will I receive the material?", a: "Immediately after your purchase, you'll receive instructions for accessing the War Room. The volumes and bonus materials will be available in downloadable formats so you can use them on your computer, tablet, or phone." },
  { q: "Is this for orthodontic students or practicing orthodontists?", a: "BIOMECHANICS WAR ROOM™ was designed primarily for orthodontic residents, advanced orthodontic students, orthodontists in training, and practicing professionals who want additional structured practice with biomechanical reasoning." },
  { q: "Do I need advanced biomechanics knowledge?", a: "You don't need to know every concept before starting. The challenges are organized progressively and each case includes a structured analysis framework and an explanation afterward. However, this is an educational training resource. It does not replace formal orthodontic education, clinical supervision, scientific literature, or individualized professional judgment." },
  { q: "Are the cases intended to provide universal clinical protocols?", a: "No. The cases are educational scenarios designed to train biomechanical reasoning. Real clinical decisions must always be adapted to the individual patient, diagnosis, treatment objectives, professional training, applicable standards, and current evidence." },
  { q: "Can I download the materials?", a: "Yes. The included materials will be available in the downloadable formats specified with your purchase." },
  { q: "How long will I have access?", a: "Your purchase includes lifetime access to the materials, subject to the terms of the delivery platform." },
  { q: "Is this a course?", a: "No. BIOMECHANICS WAR ROOM™ is primarily a case-based training and reasoning system built around challenges, decision maps, worksheets, and review tools. You don't need to complete hours of recorded videos to use it." },
  { q: "Is this a one-time payment?", a: "Yes. The special launch price is $27 USD with no recurring subscription." },
  { q: "What if I don't like it?", a: "Your purchase is protected by a 7-day money-back guarantee. If BIOMECHANICS WAR ROOM™ isn't the right fit for you, you can request a refund within the applicable guarantee period." },
];

export const recentBuyers = [
  { name: "María G.", country: "Mexico" },
  { name: "Andrés R.", country: "Colombia" },
  { name: "Lucía F.", country: "Spain" },
  { name: "Javier M.", country: "Argentina" },
  { name: "Camila P.", country: "Chile" },
  { name: "Diego S.", country: "Peru" },
  { name: "Valentina C.", country: "Ecuador" },
  { name: "Sofía L.", country: "Uruguay" },
  { name: "Carlos H.", country: "Guatemala" },
  { name: "Daniela V.", country: "Costa Rica" },
  { name: "Alejandro T.", country: "Dominican Republic" },
  { name: "Gabriela N.", country: "Venezuela" },
  { name: "Mateo B.", country: "Bolivia" },
  { name: "Isabel O.", country: "Paraguay" },
  { name: "Fernando Q.", country: "Panama" },
];
